import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    setTimeout(() => {
      toast({
        title: t.contact.success,
        description: t.language === 'en' ? 'We will get back to you soon.' : 'Nous vous recontacterons bientôt.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-5xl md:text-6xl font-bold mb-4 text-center">{t.contact.title}</h1>
        <p className="text-xl text-gray-600 mb-12 text-center">{t.contact.subtitle}</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold mb-6">{t.contact.info}</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black text-white rounded-lg">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">{t.contact.phone}</p>
                  <p className="text-gray-600">+33 6 64 25 28 13</p>
                  <p className="text-gray-600">+61 480 470 621</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black text-white rounded-lg">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">{t.contact.email}</p>
                  <a href="mailto:delfieux.alexi@gmail.com" className="text-gray-600 hover:text-black transition-colors">
                    delfieux.alexi@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black text-white rounded-lg">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">{t.contact.basedIn}</p>
                  <p className="text-gray-600">Paris, France</p>
                  <p className="text-gray-600">Melbourne, Australia</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black text-white rounded-lg">
                  <Instagram className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">Instagram</p>
                  <a 
                    href="https://instagram.com/mozer.paint" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-black transition-colors"
                  >
                    @mozer.paint
                  </a>
                </div>
              </div>
            </div>

            {/* Commission Section */}
            <div className="mt-12 bg-gradient-to-br from-orange-50 to-blue-50 rounded-lg p-6">
              <h3 className="text-2xl font-bold mb-3">{t.contact.commission}</h3>
              <p className="text-gray-700 leading-relaxed">{t.contact.commissionText}</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.name}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.phone}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent transition-all resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t.contact.sending : t.contact.send}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
